"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Users2,
  Building2,
  Briefcase,
  Target,
  TrendingUp,
  Calendar,
  ListChecks,
  PieChart,
  Mail,
  Smartphone,
  Workflow,
  Filter,
  FileSpreadsheet,
  Trophy,
  ArrowLeft,
  PlayCircle,
  BarChart3,
  ShieldCheck,
  Zap,
  Sparkles,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
} from "@/components/landing/solutions/SolutionSections";

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1fr_1.05fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>Sales OS · فارسی و یکپارچه</HeroBadge>
            <HeroTitle pre="CRM فارسی،" gradient="برای رشد جدی" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              مخاطبین، شرکت‌ها، فرصت‌های فروش، وظایف، تقویم، کمپین ایمیل و پیامک، اتوماسیون و گزارش
              درآمد — همه در یک پلتفرم.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                شروع رایگان <ArrowLeft size={18} />
              </Link>
              <Link
                href="/dashboard/crm"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">دموی داشبورد</span>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> RTL ۱۰۰٪
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-secondary-main" /> ۱۹+ ماژول
              </span>
            </div>
          </div>
          <KanbanMock />
        </div>
      </section>

      <GiantStats
        items={[
          { value: "+۳۸٪", label: "نرخ بستن فرصت" },
          { value: "+۲.۲x", label: "بهره‌وری فروشنده" },
          { value: "۱۹+", label: "ماژول CRM" },
          { value: "۱۰۰٪", label: "RTL و فارسی" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="ماژول‌ها" title="هر چه برای Sales OS لازم دارید" />
        <LineFeatures
          items={[
            {
              icon: Users2,
              title: "مخاطبین و شرکت‌ها",
              desc: "پروفایل کامل با تاریخچهٔ همه‌ی تماس‌ها، B2B با ساختار شعب.",
            },
            {
              icon: Briefcase,
              title: "Pipeline و فرصت‌ها",
              desc: "Kanban بصری با مرحله‌های قابل تنظیم و Drag & Drop.",
            },
            {
              icon: ListChecks,
              title: "وظایف و یادآوری",
              desc: "هیچ تماسی فراموش نشه — تقویم تیمی هفتگی.",
            },
            { icon: Mail, title: "کمپین ایمیل", desc: "ادیتور بصری، A/B تست و گزارش لحظه‌ای." },
            { icon: Smartphone, title: "کمپین پیامک", desc: "ارسال انبوه با شخصی‌سازی و سگمنت." },
            { icon: Workflow, title: "اتوماسیون", desc: "If-Then برای فالوآپ، امتیازدهی و Drip." },
            { icon: PieChart, title: "گزارش درآمد", desc: "MRR، Pipeline، Forecast و Funnel." },
            { icon: Trophy, title: "Leaderboard", desc: "رقابت سالم بین فروشندگان." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="Pipeline بصری"
          title={
            <>
              Drag & Drop، <span className="brand-gradient-text">Forecast</span> دقیق
            </>
          }
          desc="مرحله‌های فروش رو خودت طراحی کن. هر کارت، یک فرصت با حساب احتمالی درآمد."
          bullets={["مرحله‌های قابل تنظیم", "احتمال موفقیت هر فرصت", "Forecast ماهانه خودکار"]}
          slot={<MiniKanban />}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="هوش مصنوعی"
          title={
            <>
              Lead Scoring با <span className="brand-gradient-text">ترندی</span>
            </>
          }
          desc="ترندی به‌طور خودکار سرنخ‌های داغ رو شناسایی و برچسب می‌زنه تا تیم فروش روی پربازده‌ها تمرکز کنه."
          bullets={["امتیازدهی خودکار ۰ تا ۱۰۰", "تشخیص سیگنال خرید", "اولویت‌بندی روزانه تیم"]}
          slot={<MockLeadScore />}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="چطور شروع کنم" title="در یک هفته راه می‌افتی" />
        <StepsTimeline
          steps={[
            { title: "Import مخاطبین", desc: "از اکسل، گوگل و سایر CRMها." },
            { title: "تعریف Pipeline", desc: "مرحله‌های فروش خودت." },
            { title: "اتصال کانال‌ها", desc: "اینستا، ایمیل، پیامک، وب." },
            { title: "اتوماسیون و گزارش", desc: "فالوآپ خودکار + KPI." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مقایسه" title="ترندی در برابر CRMهای خارجی" accent="secondary" />
        <CompareSplit
          oursLabel="ترندی CRM"
          theirsLabel="CRM خارجی"
          ours={[
            "RTL و فارسی واقعی",
            "اتصال بومی به اینستا و واتس‌اپ",
            "کمپین پیامک با اپراتورهای ایران",
            "پشتیبانی فارسی ۲۴/۷",
            "قیمت ریالی و شفاف",
          ]}
          theirs={[
            "RTL سطحی، ترجمه ماشینی",
            "نیاز به افزونه و دردسر",
            "بدون پشتیبانی پیامک ایران",
            "تیکت انگلیسی، با تأخیر",
            "هزینه دلاری و نوسان نرخ",
          ]}
        />
      </Section>

      <Section>
        <BigQuote
          quote="Forecast ترندی به ما کمک کرد ماه به ماه دقیق برنامه‌ریزی کنیم. از ۳ ابزار جدا به یک پلتفرم کوچ کردیم."
          name="مهسا قاسمی"
          role="مدیر فروش · آرتین تک"
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مشتریان" title="تیم‌هایی که جلو زدند" />
        <MiniTestimonials
          items={[
            {
              name: "حمید زارع",
              role: "Founder · Kavoosh",
              quote: "از ۳ ابزار به یک ترندی کوچ کردیم؛ تیم سریع‌تر شد.",
            },
            {
              name: "نازنین یوسفی",
              role: "B2B SaaS",
              quote: "Lead Scoring ترندی واقعاً کار می‌کنه.",
            },
            { name: "Reza M.", role: "Real Estate", quote: "Pipeline RTL was the missing piece." },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات متداول" />
        <FAQ
          items={[
            { q: "Import از CRM دیگه می‌تونم؟", a: "بله، از HubSpot، Zoho، Pipedrive و فایل CSV." },
            { q: "نقش‌های تیم چطور تنظیم می‌شه؟", a: "هر فیلد و ماژول، سطح دسترسی جداگانه دارد." },
            { q: "API داره؟", a: "بله، REST و Webhook کامل." },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            تیم فروشت آماده‌ست <span className="brand-gradient-text">جهش</span> کنه؟
          </>
        }
        desc="رایگان شروع کن، اول ماه پرداخت."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

function KanbanMock() {
  const cols = [
    {
      title: "سرنخ",
      color: "bg-grayScale-500",
      deals: [
        { n: "آرکا", v: "۱۲M" },
        { n: "Hilda", v: "۸M" },
      ],
    },
    { title: "گفتگو", color: "bg-primary-300", deals: [{ n: "Tehran Co", v: "۲۵M" }] },
    { title: "پیشنهاد", color: "bg-secondary-main", deals: [{ n: "DigiPlus", v: "۴۰M" }] },
    { title: "بسته‌شد", color: "bg-primary-main", deals: [{ n: "ParsTech", v: "۶۰M" }] },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-3xl border border-border bg-background shadow-2xl overflow-hidden"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-50 -z-10" />
      <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-foreground/[0.05]">
        <div className="flex items-center gap-2 text-sm font-black text-foreground">
          <Briefcase size={16} className="text-primary-main" /> Pipeline · Q۲
        </div>
        <div className="text-[10px] text-muted-foreground">۴ مرحله</div>
      </div>
      <div className="grid grid-cols-4 gap-2 p-3">
        {cols.map((c) => (
          <div
            key={c.title}
            className="rounded-xl bg-foreground/[0.04] border border-border/60 p-2 min-h-[280px]"
          >
            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-black text-foreground">
              <span className={`w-2 h-2 rounded-full ${c.color}`} /> {c.title}
            </div>
            {c.deals.map((d) => (
              <div key={d.n} className="rounded-lg bg-background border border-border p-2 mb-2">
                <div className="text-[11px] font-bold text-foreground">{d.n}</div>
                <div className="text-[10px] text-primary-main font-black mt-0.5">{d.v}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-foreground/[0.05] text-[11px]">
        <span className="text-muted-foreground">پیش‌بینی این ماه</span>
        <span className="font-black text-secondary-main flex items-center gap-1">
          <TrendingUp size={12} /> ۱۴۵M تومان
        </span>
      </div>
    </motion.div>
  );
}

function MiniKanban() {
  return <KanbanMock />;
}

function MockLeadScore() {
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <Sparkles size={16} className="text-secondary-main" /> Top Leads امروز
      </div>
      {[
        { n: "نازنین یوسفی", c: "B2B SaaS", s: 94 },
        { n: "علی محمدی", c: "آژانس آرکا", s: 87 },
        { n: "Hilda Brand", c: "Beauty", s: 72 },
      ].map((l) => (
        <div key={l.n} className="py-3 border-b border-border/60 last:border-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold text-foreground">{l.n}</div>
              <div className="text-[10px] text-muted-foreground/80">{l.c}</div>
            </div>
            <div className="text-lg font-black text-primary-main">{l.s}</div>
          </div>
          <div className="h-1.5 rounded-full bg-foreground/[0.06] overflow-hidden">
            <div
              className="h-full bg-gradient-to-l from-primary-main to-secondary-main"
              style={{ width: `${l.s}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default Page;
